import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Typography,
	CircularProgress,
	makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import axios from 'axios';
import { API_BASE_URL } from '@config';

const useStyles = makeStyles(theme => ({
	dialog: {
		'& .MuiDialog-paper': {
			minWidth: '500px'
		}
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2)
	}
}));

const SupportDialog = ({ open, onClose }) => {
	const classes = useStyles();
	const [formData, setFormData] = useState({
		subject: '',
		message: ''
	});
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState('');

	const handleChange = (field) => (e) => {
		setFormData({ ...formData, [field]: e.target.value });
		setError('');
		setSuccess(false);
	};

	const handleSubmit = async () => {
		if (!formData.subject.trim() || !formData.message.trim()) {
			setError('Please fill in all fields');
			return;
		}

		setLoading(true);
		setError('');

		try {
			await axios.post(`${API_BASE_URL}/api/support/contact`, {
				subject: formData.subject,
				message: formData.message
			});

			setSuccess(true);
			setFormData({ subject: '', message: '' });
			
			// Close dialog after 2 seconds
			setTimeout(() => {
				setSuccess(false);
				onClose();
			}, 2000);
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to send message. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			setFormData({ subject: '', message: '' });
			setError('');
			setSuccess(false);
			onClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth className={classes.dialog}>
			<DialogTitle>Contact Support</DialogTitle>
			<DialogContent>
				<div className={classes.form}>
					<Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
						Have questions or need assistance? Send us a message and we'll get back to you as soon as possible.
					</Typography>

					{error && <Alert severity="error">{error}</Alert>}
					{success && <Alert severity="success">Message sent successfully! We'll be in touch soon.</Alert>}

					<TextField
						label="Subject"
						fullWidth
						value={formData.subject}
						onChange={handleChange('subject')}
						disabled={loading || success}
						required
					/>

					<TextField
						label="Message"
						fullWidth
						multiline
						rows={6}
						value={formData.message}
						onChange={handleChange('message')}
						disabled={loading || success}
						required
						placeholder="Describe your question or issue..."
					/>
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					color="primary"
					variant="contained"
					disabled={loading || success || !formData.subject.trim() || !formData.message.trim()}
				>
					{loading ? <CircularProgress size={24} /> : 'Send Message'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SupportDialog;
